import { supabase } from './supabase';
import { ActivityService } from './activity';
import { NotificationService } from './notifications';
import type { Database } from './database.types';
import { AIAnalysisService } from './ai-analysis';
import GTMService from './gtm';
import * as Sentry from '@sentry/react';

type Creative = Database['public']['Tables']['creatives']['Row'];
type CreativeInsert = Database['public']['Tables']['creatives']['Insert'];
type CreativeUpdate = Database['public']['Tables']['creatives']['Update'];

export interface CreativeVersion {
  id: string;
  name: string;
  url: string;
  type: string;
  versionNumber: number;
  versionName?: string;
  versionNotes?: string;
  isCurrentVersion: boolean;
  createdAt: string;
  updatedAt?: string;
  fileSize?: number;
  fileType?: string;
  analysisData?: any;
  status?: string;
  familyName?: string;
  parentCreativeId?: string;
}

export interface VersionComparison {
  version1: CreativeVersion;
  version2: CreativeVersion;
  differences: {
    fileSize?: {
      old: number | null;
      new: number | null;
      change: number;
      changePercent: number;
    };
    analysisScores?: {
      old: number | null;
      new: number | null;
      change: number;
    };
    platforms?: {
      added: string[];
      removed: string[];
      common: string[];
    };
    issues?: {
      resolved: number;
      new: number;
      total: number;
    };
    platformScores?: {
      [platform: string]: {
        old: number | null;
        new: number | null;
        change: number;
      };
    };
    complianceIssues?: {
      resolved: Array<{
        platform: string;
        issue: string;
        severity: string;
      }>;
      new: Array<{
        platform: string;
        issue: string;
        severity: string;
      }>;
    };
    performanceMetrics?: {
      [metric: string]: {
        old: number | null;
        new: number | null;
        change: number;
        changePercent: number;
      };
    };
  };
}

export interface VersionStats {
  totalVersions: number;
  currentVersion: number;
  firstCreated: string;
  lastUpdated: string;
  totalAnalyses: number;
  averageScore: number;
  bestVersion: {
    version: number;
    score: number;
  } | null;
}

export interface CreateVersionRequest {
  originalCreativeId: string;
  newFileUrl: string;
  name: string;
  versionName?: string;
  versionNotes?: string;
  fileSize?: number;
  fileType?: string;
  type: string;
}

export class VersionService {
  /**
   * Get all versions of a creative group
   */
  static async getVersionHistory(creativeId: string): Promise<CreativeVersion[]> {
    try {
      // First, get the creative to determine if it's a parent or child
      const { data: creative, error: creativeError } = await supabase
        .from('creatives')
        .select('*')
        .eq('id', creativeId)
        .single();

      if (creativeError) {
        throw new Error(`Failed to get creative: ${creativeError.message}`);
      }

      // Determine the root creative ID
      const rootId = creative.parent_creative_id || creative.id;

      // Get all versions (including the root) - newest first
      const { data: versions, error } = await supabase
        .from('creatives')
        .select('*')
        .or(`id.eq.${rootId},parent_creative_id.eq.${rootId}`)
        .order('version_number', { ascending: false });

      if (error) {
        throw new Error(`Failed to get version history: ${error.message}`);
      }

      return (versions || []).map(this.mapToCreativeVersion);
    } catch (error) {
      console.error('Get version history error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getVersionHistory',
          component: 'VersionService',
        },
        extra: {
          creativeId,
        },
      });
      throw error;
    }
  }

  /**
   * Create a new version from an existing creative
   */
  static async createNewVersion(request: CreateVersionRequest, analysisData?: any, tempCreativeUrl?: string): Promise<CreativeVersion> {
    try {
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      console.log('👤 User authenticated:', user.id);

      // Check version limits before creating new version
      const { LimitEnforcementService } = await import('./limit-enforcement-service');
      const limitCheck = await LimitEnforcementService.canCreateVersion(user.id, request.originalCreativeId);
      
      if (!limitCheck.allowed) {
        console.error('❌ Version creation blocked by limits:', limitCheck.reason);
        throw new Error(limitCheck.reason || 'Version limit reached');
      }

      // Get the original creative
      const { data: originalCreative, error: originalError } = await supabase
        .from('creatives')
        .select('*')
        .eq('id', request.originalCreativeId)
        .single();

      if (originalError) {
        console.error('❌ Failed to get original creative:', originalError);
        throw new Error(`Failed to get original creative: ${originalError.message}`);
      }
      
      console.log('📁 Original creative found:', {
        id: originalCreative.id,
        name: originalCreative.name,
        parent_creative_id: originalCreative.parent_creative_id,
        version_number: originalCreative.version_number,
        project_id: originalCreative.project_id
      });

      // Determine the root creative and next version number
      const rootId = originalCreative.parent_creative_id || originalCreative.id;
      console.log('🌳 Root creative ID determined:', rootId);
      
      // Get the root creative's family name to ensure consistency across all versions
      let rootFamilyName = originalCreative.family_name;
      if (originalCreative.parent_creative_id) {
        // This is a version, need to fetch the root creative's family_name
        console.log('📁 Fetching root creative family name for consistency');
        const { data: rootCreative, error: rootError } = await supabase
          .from('creatives')
          .select('family_name')
          .eq('id', rootId)
          .single();
          
        if (rootError) {
          console.error('⚠️ Failed to fetch root creative family name:', rootError);
          console.log('🔄 Using original creative family name as fallback');
        } else {
          rootFamilyName = rootCreative.family_name;
          console.log('✅ Using root creative family name:', rootFamilyName);
        }
      } else {
        console.log('📁 Original creative is root, using its family name:', rootFamilyName);
      }
      
      // Ensure consistent family_name - if root doesn't have one, set it to the creative name
      if (!rootFamilyName || rootFamilyName.trim() === '') {
        console.log('🔧 Root creative has no family_name, setting to creative name for consistency');
        rootFamilyName = originalCreative.name;
        
        // Update the root creative to have consistent family_name for proper grouping
        const { error: updateRootError } = await supabase
          .from('creatives')
          .update({ family_name: rootFamilyName })
          .eq('id', rootId);
          
        if (updateRootError) {
          console.error('⚠️ Failed to update root creative family_name:', updateRootError);
          // Continue anyway - at least the new version will have the correct family_name
        } else {
          console.log('✅ Updated root creative family_name for consistent grouping');
        }
      }
      
      // Get the highest version number in the group
      const { data: existingVersions, error: versionsError } = await supabase
        .from('creatives')
        .select('version_number')
        .or(`id.eq.${rootId},parent_creative_id.eq.${rootId}`)
        .order('version_number', { ascending: false })
        .limit(1);

      if (versionsError) {
        console.error('❌ Failed to get existing versions:', versionsError);
        throw new Error(`Failed to get existing versions: ${versionsError.message}`);
      }

      // Calculate next version number - keep original as v1, new versions as v2, v3, etc.
      const nextVersionNumber = (existingVersions?.[0]?.version_number || 1) + 1;
      console.log('🔢 Next version number calculated:', nextVersionNumber, 'from existing versions:', existingVersions);

      // For individual creatives creating their first version, ensure original stays as v1
      if (!originalCreative.parent_creative_id && (originalCreative.version_number !== 1 || originalCreative.version_number === null)) {
        console.log('🆕 Setting original creative to version 1 (first versioning)');
        const { error: updateOriginalError } = await supabase
          .from('creatives')
          .update({ 
            version_number: 1,
            family_name: rootFamilyName, // Ensure consistent family name
            is_current_version: false // No longer current since we're creating a new version
          })
          .eq('id', originalCreative.id);
          
        if (updateOriginalError) {
          console.error('❌ Failed to update original creative version number:', updateOriginalError);
          throw new Error(`Failed to update original creative: ${updateOriginalError.message}`);
        }
        console.log('✅ Original creative updated to v1 with consistent family name');
      } else if (!originalCreative.parent_creative_id) {
        // Just mark as not current and ensure consistent family name
        console.log('🔄 Marking original creative as not current and ensuring consistent family name');
        const { error: updateCurrentError } = await supabase
          .from('creatives')
          .update({ 
            is_current_version: false,
            family_name: rootFamilyName // Ensure consistent family name
          })
          .eq('id', originalCreative.id);
          
        if (updateCurrentError) {
          console.error('❌ Failed to update original creative current flag:', updateCurrentError);
        } else {
          console.log('✅ Original creative updated with consistent family name');
        }
      }
      
      // Always clear current version flags before creating new version to avoid constraint violations
      console.log('🔄 Clearing current version flags for all versions in family...');
      const { error: clearCurrentVersionError } = await supabase
        .from('creatives')
        .update({ is_current_version: false })
        .or(`id.eq.${rootId},parent_creative_id.eq.${rootId}`);

      // Create the new version
      const newVersionData: CreativeInsert = {
        name: request.name,
        family_name: rootFamilyName, // Use root creative's family name for consistency
        url: request.newFileUrl,
        type: request.type,
        parent_creative_id: rootId,
        version_number: nextVersionNumber,
        version_name: request.versionName || null,
        version_notes: request.versionNotes || null,
        is_current_version: true,
        file_size: request.fileSize || null,
        file_type: request.fileType || null,
        project_id: originalCreative.project_id, // Inherit project from original
        user_id: user.id,
        status: analysisData ? 'analyzed' : 'uploaded',
        analysis_data: analysisData || null
      };

      console.log('📝 Creating new version with data:', {
        ...newVersionData,
        parent_creative_id: rootId,
        version_number: nextVersionNumber,
        project_id: originalCreative.project_id
      });

      const { data: newVersion, error: createError } = await supabase
        .from('creatives')
        .insert(newVersionData)
        .select()
        .single();

      if (createError) {
        console.error('❌ Failed to create new version:', createError);
        throw new Error(`Failed to create new version: ${createError.message}`);
      }


      // Log version creation activity
      if (originalCreative.project_id) {
        try {
          await ActivityService.logVersionCreated(
            originalCreative.project_id,
            newVersion.id,
            newVersion.name,
            nextVersionNumber
          );

          // Create notification for version creation
          if (originalCreative.user_id) {
            await NotificationService.createVersionCreatedNotification(
              originalCreative.user_id,
              newVersion.name,
              nextVersionNumber,
              newVersion.id
            );
          }
        } catch (activityError) {
          console.warn('⚠️ Failed to log version creation activity:', activityError);
          // Don't fail the entire operation if activity logging fails
        }
      }

      // If we have analysis data, transfer the existing analysis record to the version with retry logic
      if (analysisData && analysisData.analysisId) {
        let transferSuccess = false;
        let retries = 3;
        
        console.log('🔄 Attempting to transfer analysis record to version with retry logic');
        
        while (!transferSuccess && retries > 0) {
          try {
            // First verify the analysis exists
            const { data: analysisCheck, error: checkError } = await supabase
              .from('analyses')
              .select('id, creative_id')
              .eq('id', analysisData.analysisId)
              .single();
              
            if (checkError || !analysisCheck) {
              console.warn(`⚠️ Analysis ${analysisData.analysisId} not found, retry ${4-retries}/3`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              retries--;
              continue;
            }
            
            console.log('📊 Found analysis record:', {
              id: analysisCheck.id,
              currentCreativeId: analysisCheck.creative_id,
              targetCreativeId: newVersion.id
            });
            
            // Update the analysis record to point to the new version
            const { error: analysisError } = await supabase
              .from('analyses')
              .update({
                creative_id: newVersion.id,
                creative_url: newVersion.url,
                file_name: newVersion.name,
                updated_at: new Date().toISOString()
              })
              .eq('id', analysisData.analysisId);

            if (!analysisError) {
              transferSuccess = true;
              console.log('✅ Analysis record transferred to version successfully');
            } else {
              console.error(`❌ Transfer attempt ${4-retries} failed:`, analysisError);
              retries--;
              if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          } catch (error) {
            console.error(`❌ Transfer attempt ${4-retries} error:`, error);
            retries--;
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
        
        if (!transferSuccess) {
          console.error('❌ Failed to transfer analysis after all retries');
          // Analysis transfer failed but version was created - this might cause double analysis later
          console.warn('⚠️ Version created but analysis not transferred - may cause double analysis');
        }
      }

      // Track version creation
      GTMService.trackCreateVersion(
        newVersionData.parent_creative_id || request.originalCreativeId,
        newVersion.id,
        nextVersionNumber,
        originalCreative.id,
        originalCreative.project_id || undefined,
        user.id
      );

      return this.mapToCreativeVersion(newVersion);
    } catch (error) {
      console.error('❌ VersionService.createNewVersion failed:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        request: request
      });
      Sentry.captureException(error, {
        tags: {
          operation: 'createNewVersion',
          component: 'VersionService',
        },
        extra: {
          request,
          originalCreativeId: request.originalCreativeId,
          versionName: request.versionName,
        },
      });
      throw error;
    }
  }

  /**
   * Compare two versions
   */
  static async compareVersions(version1Id: string, version2Id: string): Promise<VersionComparison> {
    try {
      
      const { data: versions, error } = await supabase
        .from('creatives')
        .select('*')
        .in('id', [version1Id, version2Id]);

      if (error) {
        throw new Error(`Failed to get versions for comparison: ${error.message}`);
      }

      if (!versions || versions.length !== 2) {
        throw new Error('Could not find both versions for comparison');
      }

      const version1Raw = versions.find(v => v.id === version1Id)!;
      const version2Raw = versions.find(v => v.id === version2Id)!;
      
      console.log('📊 Raw version data from database:', {
        version1: {
          id: version1Raw.id,
          name: version1Raw.name,
          file_size: version1Raw.file_size,
          file_type: version1Raw.file_type,
          version_number: version1Raw.version_number,
          analysis_data_exists: !!version1Raw.analysis_data
        },
        version2: {
          id: version2Raw.id,
          name: version2Raw.name,
          file_size: version2Raw.file_size,
          file_type: version2Raw.file_type,
          version_number: version2Raw.version_number,
          analysis_data_exists: !!version2Raw.analysis_data
        }
      });

      const version1 = this.mapToCreativeVersion(version1Raw);
      const version2 = this.mapToCreativeVersion(version2Raw);

      console.log('📊 Mapped version data:', {
        version1: {
          id: version1.id,
          name: version1.name,
          fileSize: version1.fileSize,
          fileType: version1.fileType,
          versionNumber: version1.versionNumber,
          analysisData_exists: !!version1.analysisData
        },
        version2: {
          id: version2.id,
          name: version2.name,
          fileSize: version2.fileSize,
          fileType: version2.fileType,
          versionNumber: version2.versionNumber,
          analysisData_exists: !!version2.analysisData
        }
      });

      // First, try to fix any missing creative_id relationships
      console.log('🔧 Checking and fixing creative-analysis relationships...');
      try {
        await AIAnalysisService.fixMissingCreativeIds();
      } catch (fixError) {
        console.warn('⚠️ Failed to fix missing creative IDs:', fixError);
      }

      // Try to get enhanced analysis data from the analyses table
      console.log('🔍 Fetching enhanced analysis data...');
      const [enhancedAnalysis1, enhancedAnalysis2] = await Promise.all([
        AIAnalysisService.getAnalysisForCreative(version1Id).catch(err => {
          console.log('⚠️ Failed to get enhanced analysis for version1:', err.message);
          return null;
        }),
        AIAnalysisService.getAnalysisForCreative(version2Id).catch(err => {
          console.log('⚠️ Failed to get enhanced analysis for version2:', err.message);
          return null;
        })
      ]);

      // Use enhanced data if available, otherwise fall back to stored analysis data
      // Apply the same fix as in version history - properly handle missing analysis data
      let analysisData1 = null;
      let analysisData2 = null;

      // For version 1
      if (version1.analysisData && version1.analysisData.overallScore) {
        analysisData1 = version1.analysisData;
        console.log('✅ Version 1: Using analysis data from creative record');
      } else if (enhancedAnalysis1 && enhancedAnalysis1.overallScore) {
        analysisData1 = enhancedAnalysis1;
        console.log('✅ Version 1: Using analysis data from analyses table');
      } else {
        console.log('❌ Version 1: No analysis data available');
      }

      // For version 2  
      if (version2.analysisData && version2.analysisData.overallScore) {
        analysisData2 = version2.analysisData;
        console.log('✅ Version 2: Using analysis data from creative record');
      } else if (enhancedAnalysis2 && enhancedAnalysis2.overallScore) {
        analysisData2 = enhancedAnalysis2;
        console.log('✅ Version 2: Using analysis data from analyses table');
      } else {
        console.log('❌ Version 2: No analysis data available');
      }

      console.log('🔄 Version comparison final analysis data summary:', {
        version1Id,
        version2Id,
        finalData1Present: !!analysisData1,
        finalData2Present: !!analysisData2,
        analysis1Score: analysisData1?.overallScore,
        analysis2Score: analysisData2?.overallScore
      });

      // Update version objects with fetched analysis data
      if (analysisData1) {
        version1.analysisData = analysisData1;
        console.log('✅ Updated version1 with fetched analysis data');
      }
      if (analysisData2) {
        version2.analysisData = analysisData2;
        console.log('✅ Updated version2 with fetched analysis data');
      }

      // Calculate differences
      const differences: VersionComparison['differences'] = {};

      // File size comparison
      console.log('📏 File size comparison:', {
        version1Size: version1.fileSize,
        version2Size: version2.fileSize,
        version1SizeType: typeof version1.fileSize,
        version2SizeType: typeof version2.fileSize
      });
      
      if (version1.fileSize !== null && version1.fileSize !== undefined || 
          version2.fileSize !== null && version2.fileSize !== undefined) {
        const oldSize = version1.fileSize || 0;
        const newSize = version2.fileSize || 0;
        const change = newSize - oldSize;
        const changePercent = oldSize > 0 ? (change / oldSize) * 100 : 0;

        differences.fileSize = {
          old: version1.fileSize || null,
          new: version2.fileSize || null,
          change,
          changePercent
        };
        
        console.log('📏 File size differences calculated:', differences.fileSize);
      } else {
        console.log('⚠️ No file size data available for comparison');
      }

      // Analysis scores comparison (handle partial analysis data)
      if (analysisData1 || analysisData2) {
        const oldScore = analysisData1?.overallScore || null;
        const newScore = analysisData2?.overallScore || null;
        const change = (newScore || 0) - (oldScore || 0);

        differences.analysisScores = {
          old: oldScore,
          new: newScore,
          change
        };
        
        console.log('📊 Analysis scores calculated (with partial data):', {
          oldScore,
          newScore,
          change,
          analysisData1Present: !!analysisData1,
          analysisData2Present: !!analysisData2,
          analysisScores: differences.analysisScores
        });

        // Platform comparison (handle partial analysis data)
        const platforms1 = Object.keys(analysisData1?.platforms || {});
        const platforms2 = Object.keys(analysisData2?.platforms || {});
        const allPlatforms = [...new Set([...platforms1, ...platforms2])];
        
        differences.platforms = {
          added: platforms2.filter(p => !platforms1.includes(p)),
          removed: platforms1.filter(p => !platforms2.includes(p)),
          common: platforms1.filter(p => platforms2.includes(p))
        };

        // Platform scores comparison (handle partial data)
        differences.platformScores = {};
        allPlatforms.forEach(platform => {
          const oldPlatformData = analysisData1?.platforms?.[platform];
          const newPlatformData = analysisData2?.platforms?.[platform];
          const oldScore = oldPlatformData?.overallScore || null;
          const newScore = newPlatformData?.overallScore || null;
          
          if (oldScore !== null || newScore !== null) {
            differences.platformScores![platform] = {
              old: oldScore,
              new: newScore,
              change: (newScore || 0) - (oldScore || 0)
            };
          }
        });

        // Compliance issues comparison
        const getComplianceIssues = (data: any) => {
          const issues: Array<{platform: string, issue: string, severity: string}> = [];
          Object.entries(data.platforms || {}).forEach(([platform, platformData]: [string, any]) => {
            (platformData.compliance?.issues || []).forEach((issue: any) => {
              issues.push({
                platform,
                issue: issue.issue || issue.description || 'Unknown issue',
                severity: issue.severity || 'medium'
              });
            });
          });
          return issues;
        };

        const issues1 = getComplianceIssues(analysisData1 || {});
        const issues2 = getComplianceIssues(analysisData2 || {});

        // Find resolved and new issues
        const resolvedIssues = issues1.filter(issue1 => 
          !issues2.some(issue2 => 
            issue2.platform === issue1.platform && 
            issue2.issue === issue1.issue
          )
        );

        const newIssues = issues2.filter(issue2 => 
          !issues1.some(issue1 => 
            issue1.platform === issue2.platform && 
            issue1.issue === issue2.issue
          )
        );

        differences.complianceIssues = {
          resolved: resolvedIssues,
          new: newIssues
        };

        differences.issues = {
          resolved: resolvedIssues.length,
          new: newIssues.length,
          total: issues2.length
        };

        // Performance metrics comparison
        differences.performanceMetrics = {};
        
        // Compare engagement scores
        const getEngagementScore = (data: any) => {
          const scores = Object.values(data.platforms || {}).map((p: any) => p.performance?.engagement || 0);
          return scores.length > 0 ? scores.reduce((a: number, b: number) => a + b, 0) / scores.length : 0;
        };

        const oldEngagement = getEngagementScore(analysisData1 || {});
        const newEngagement = getEngagementScore(analysisData2 || {});
        
        if (oldEngagement > 0 || newEngagement > 0) {
          differences.performanceMetrics.engagement = {
            old: oldEngagement || null,
            new: newEngagement || null,
            change: newEngagement - oldEngagement,
            changePercent: oldEngagement > 0 ? ((newEngagement - oldEngagement) / oldEngagement) * 100 : 0
          };
        }

        // Compare conversion potential
        const getConversionScore = (data: any) => {
          const scores = Object.values(data.platforms || {}).map((p: any) => p.performance?.conversion || 0);
          return scores.length > 0 ? scores.reduce((a: number, b: number) => a + b, 0) / scores.length : 0;
        };

        const oldConversion = getConversionScore(analysisData1 || {});
        const newConversion = getConversionScore(analysisData2 || {});
        
        if (oldConversion > 0 || newConversion > 0) {
          differences.performanceMetrics.conversion = {
            old: oldConversion || null,
            new: newConversion || null,
            change: newConversion - oldConversion,
            changePercent: oldConversion > 0 ? ((newConversion - oldConversion) / oldConversion) * 100 : 0
          };
        }
      }

      // Track version comparison
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        GTMService.trackCompareVersions(
          version1Raw.parent_creative_id || version1Id,
          version1Id,
          'side_by_side', // Default comparison type
          version1Raw.project_id || undefined,
          user.id
        );
      }

      const result = {
        version1,
        version2,
        differences
      };
      
      console.log('✅ Version comparison completed:', {
        version1: {
          id: result.version1.id,
          fileSize: result.version1.fileSize,
          hasAnalysisData: !!result.version1.analysisData,
          analysisScore: result.version1.analysisData?.overallScore
        },
        version2: {
          id: result.version2.id,
          fileSize: result.version2.fileSize,
          hasAnalysisData: !!result.version2.analysisData,
          analysisScore: result.version2.analysisData?.overallScore
        },
        differences: {
          fileSize: result.differences.fileSize,
          analysisScores: result.differences.analysisScores,
          hasOtherDifferences: Object.keys(result.differences).length > 0
        }
      });
      
      return result;
    } catch (error) {
      console.error('Compare versions error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'compareVersions',
          component: 'VersionService',
        },
        extra: {
          version1Id,
          version2Id,
        },
      });
      throw error;
    }
  }

  /**
   * Set current version (rollback functionality)
   */
  static async setCurrentVersion(creativeId: string): Promise<void> {
    try {
      // Get the creative to determine the root
      const { data: creative, error: creativeError } = await supabase
        .from('creatives')
        .select('*')
        .eq('id', creativeId)
        .single();

      if (creativeError) {
        throw new Error(`Failed to get creative: ${creativeError.message}`);
      }

      const rootId = creative.parent_creative_id || creative.id;

      // Set all versions to not current
      await supabase
        .from('creatives')
        .update({ is_current_version: false })
        .or(`id.eq.${rootId},parent_creative_id.eq.${rootId}`);

      // Set the specified version as current
      const { error: updateError } = await supabase
        .from('creatives')
        .update({ is_current_version: true })
        .eq('id', creativeId);

      if (updateError) {
        throw new Error(`Failed to set current version: ${updateError.message}`);
      }

      // Track version restoration
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        GTMService.trackRestoreVersion(
          rootId,
          creativeId,
          creative.version_number || 1,
          creative.project_id || undefined,
          user.id
        );
      }
    } catch (error) {
      console.error('Set current version error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'setCurrentVersion',
          component: 'VersionService',
        },
        extra: {
          creativeId,
        },
      });
      throw error;
    }
  }

  /**
   * Get version statistics for a creative group
   */
  static async getVersionStats(creativeId: string): Promise<VersionStats> {
    try {
      // Get the creative to determine the root
      const { data: creative, error: creativeError } = await supabase
        .from('creatives')
        .select('*')
        .eq('id', creativeId)
        .single();

      if (creativeError) {
        throw new Error(`Failed to get creative: ${creativeError.message}`);
      }

      const rootId = creative.parent_creative_id || creative.id;

      // Get all versions (newest first for consistency)
      const { data: versions, error } = await supabase
        .from('creatives')
        .select('*')
        .or(`id.eq.${rootId},parent_creative_id.eq.${rootId}`)
        .order('version_number', { ascending: false });

      if (error) {
        throw new Error(`Failed to get versions: ${error.message}`);
      }

      if (!versions || versions.length === 0) {
        throw new Error('No versions found');
      }

      // Calculate statistics
      const totalVersions = versions.length;
      const currentVersionData = versions.find(v => v.is_current_version);
      const currentVersion = currentVersionData?.version_number || 1;
      
      const firstCreated = versions[0].created_at || '';
      const lastUpdated = versions.reduce((latest, v) => {
        const versionDate = v.updated_at || v.created_at || '';
        return versionDate > latest ? versionDate : latest;
      }, '');

      // Count analyses and calculate average score
      const versionsWithAnalysis = versions.filter(v => v.analysis_data);
      const totalAnalyses = versionsWithAnalysis.length;
      
      let averageScore = 0;
      let bestVersion = null;

      if (totalAnalyses > 0) {
        const scores = versionsWithAnalysis.map(v => {
          const analysisData = v.analysis_data as any;
          return analysisData?.overallScore || 0;
        });
        averageScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        
        const bestScore = Math.max(...scores);
        const bestVersionData = versionsWithAnalysis.find(v => {
          const analysisData = v.analysis_data as any;
          return (analysisData?.overallScore || 0) === bestScore;
        });
        
        if (bestVersionData) {
          bestVersion = {
            version: bestVersionData.version_number || 1,
            score: bestScore
          };
        }
      }

      return {
        totalVersions,
        currentVersion,
        firstCreated,
        lastUpdated,
        totalAnalyses,
        averageScore: Math.round(averageScore),
        bestVersion
      };
    } catch (error) {
      console.error('Get version stats error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getVersionStats',
          component: 'VersionService',
        },
        extra: {
          creativeId,
        },
      });
      throw error;
    }
  }

  /**
   * Get the current version of a creative group
   */
  static async getCurrentVersion(creativeId: string): Promise<CreativeVersion | null> {
    try {
      // Get the creative to determine the root
      const { data: creative, error: creativeError } = await supabase
        .from('creatives')
        .select('*')
        .eq('id', creativeId)
        .single();

      if (creativeError) {
        return null;
      }

      const rootId = creative.parent_creative_id || creative.id;

      // Get the current version
      const { data: currentVersion, error } = await supabase
        .from('creatives')
        .select('*')
        .or(`id.eq.${rootId},parent_creative_id.eq.${rootId}`)
        .eq('is_current_version', true)
        .single();

      if (error || !currentVersion) {
        return null;
      }

      return this.mapToCreativeVersion(currentVersion);
    } catch (error) {
      console.error('Get current version error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getCurrentVersion',
          component: 'VersionService',
        },
        extra: {
          creativeId,
        },
      });
      return null;
    }
  }

  /**
   * Check if a creative has multiple versions
   */
  static async hasMultipleVersions(creativeId: string): Promise<boolean> {
    try {
      const versions = await this.getVersionHistory(creativeId);
      return versions.length > 1;
    } catch (error) {
      console.error('Check multiple versions error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'hasMultipleVersions',
          component: 'VersionService',
        },
        extra: {
          creativeId,
        },
      });
      return false;
    }
  }

  /**
   * Delete a version (cannot delete if it's the only version or current version)
   */
  static async deleteVersion(creativeId: string): Promise<void> {
    try {
      const versions = await this.getVersionHistory(creativeId);
      
      if (versions.length <= 1) {
        throw new Error('Cannot delete the only version of a creative');
      }

      const versionToDelete = versions.find(v => v.id === creativeId);
      if (!versionToDelete) {
        throw new Error('Version not found');
      }

      if (versionToDelete.isCurrentVersion) {
        throw new Error('Cannot delete the current version. Set another version as current first.');
      }

      // Delete the version
      const { error } = await supabase
        .from('creatives')
        .delete()
        .eq('id', creativeId);

      if (error) {
        throw new Error(`Failed to delete version: ${error.message}`);
      }

      // Track version deletion
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        GTMService.trackDeleteVersion(
          versionToDelete.id.substring(0, 8), // Use first 8 chars as creative identifier
          creativeId,
          versionToDelete.versionNumber,
          undefined, // Project ID not available in this context
          user.id
        );
      }
    } catch (error) {
      console.error('Delete version error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'deleteVersion',
          component: 'VersionService',
        },
        extra: {
          creativeId,
        },
      });
      throw error;
    }
  }

  /**
   * Debug method to test version comparison functionality
   */
  static async debugVersionComparison(version1Id: string, version2Id: string): Promise<void> {
    try {
      console.log('🐛 Starting version comparison debug for:', { version1Id, version2Id });
      
      // Check if versions exist
      const { data: versions, error } = await supabase
        .from('creatives')
        .select('*')
        .in('id', [version1Id, version2Id]);
        
      if (error) {
        console.error('❌ Database error:', error);
        return;
      }
      
      console.log('📦 Found versions:', versions?.map(v => ({
        id: v.id,
        name: v.name,
        file_size: v.file_size,
        file_type: v.file_type,
        has_analysis: !!v.analysis_data,
        version_number: v.version_number
      })));
      
      // Check analysis records
      const { data: analyses, error: analysisError } = await supabase
        .from('analyses')
        .select('id, creative_id, creative_url, file_name')
        .or(`creative_id.eq.${version1Id},creative_id.eq.${version2Id}`);
        
      if (analysisError) {
        console.error('❌ Analysis query error:', analysisError);
      } else {
        console.log('📊 Found analysis records:', analyses);
      }
      
      // Test the actual comparison
      try {
        const comparison = await this.compareVersions(version1Id, version2Id);
        console.log('✅ Comparison successful:', {
          version1FileSize: comparison.version1.fileSize,
          version2FileSize: comparison.version2.fileSize,
          version1Analysis: !!comparison.version1.analysisData,
          version2Analysis: !!comparison.version2.analysisData,
          differences: Object.keys(comparison.differences)
        });
      } catch (compError) {
        console.error('❌ Comparison failed:', compError);
      }
      
    } catch (error) {
      console.error('❌ Debug method failed:', error);
    }
  }

  /**
   * Helper method to map database row to CreativeVersion interface
   */
  private static mapToCreativeVersion(creative: Creative): CreativeVersion {
    const mapped = {
      id: creative.id,
      name: creative.name,
      url: creative.url,
      type: creative.type,
      versionNumber: creative.version_number || 1,
      versionName: creative.version_name || undefined,
      versionNotes: creative.version_notes || undefined,
      isCurrentVersion: creative.is_current_version || false,
      createdAt: creative.created_at || '',
      updatedAt: creative.updated_at || undefined,
      fileSize: creative.file_size ?? undefined, // Use nullish coalescing to handle null/undefined properly
      fileType: creative.file_type || undefined,
      analysisData: creative.analysis_data || undefined,
      status: creative.status || undefined,
      familyName: creative.family_name || undefined,
      parentCreativeId: creative.parent_creative_id || undefined
    };
    
    console.log('🗺️ Mapping creative to version:', {
      creativeId: creative.id,
      rawFileSize: creative.file_size,
      mappedFileSize: mapped.fileSize,
      rawAnalysisData: !!creative.analysis_data,
      mappedAnalysisData: !!mapped.analysisData
    });
    
    return mapped;
  }
}

export default VersionService; 